import { useState } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { TextInput, anchorTo, useAnchorRect, useDismiss } from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A colour. Just the colour — item 22, second pass.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TWENTY SWATCHES ARE GONE, AND THE FIRST PASS KEEPING THEM AS "PRESETS" WAS THE
 * MISREADING.** Item 22 asked for a colour picker; what shipped was the old palette grid with a
 * wheel hidden behind an *Egyéni szín* button — so the screen still opened on twenty tiles and the
 * thing that was asked for took a click to reach. The product owner's second word on it is
 * unambiguous: *"I just want the colour picker defaulting to a colour that can be changed, no need
 * for the presets."*
 *
 * ⚠️ **`presets` is gone from the props as well as from the screen**, rather than defaulted to an
 * empty array. A prop nothing passes is a prop somebody re-populates six months later without
 * knowing what it was removed for; `vocabulary-section.tsx` and `specimen.tsx` are updated with
 * this file.
 *
 * ⚠️ **`PROJECT_COLORS` and `STATUS_COLORS` stay in `shared`, and still matter.** They are what
 * seeds a NEW row's colour, so a project created without touching this control is still one of the
 * product's own colours rather than black. What they are no longer is a constraint or a menu.
 *
 * ⚠️ **`react-colorful`, not a hand-rolled wheel.** 2.8 kB, no dependencies, MIT — and the part
 * nobody writes correctly by hand is the pointer maths under a dragging finger.
 */
export function ColorPicker({
  value,
  onChange,
  label,
  hint,
}: {
  value: string;
  onChange: (color: string) => void;
  /** Already translated — "Szín". */
  label: string;
  /** ⚠️ Optional since item 10: *"how it appears in lists"* said nothing the colour did not. */
  hint?: string;
}) {
  const t = useT();
  /**
   * 26 Oct 2026 · ⚠️ **THE GRADIENT IS A POPOVER NOW, OPENED BY THE SWATCH.** The product owner: the
   * field shows only the colour and its hex; pressing the colour opens the full picker. Drawn
   * inline it took 150px of every dialog that carries a colour. The kit's own popover pieces —
   * the date picker's — so it positions, stays on screen and closes on an outside press alike.
   */
  const [open, setOpen] = useState(false);
  const { surface, trigger } = useDismiss(open, () => {
    setOpen(false);
  });
  const { rect, measure } = useAnchorRect(open, trigger);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium">{label}</span>
        <div className="flex-1" />
        {hint === undefined ? null : (
          <span className="text-muted-foreground text-[11px]">{hint}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div ref={trigger}>
          <button
            type="button"
            aria-label={t('common.color.open')}
            aria-expanded={open}
            className="focus-visible:ring-ring/50 block size-8 shrink-0 rounded-md border outline-none focus-visible:ring-[3px]"
            style={{ background: value }}
            onClick={() => {
              if (open) {
                setOpen(false);
              } else {
                measure();
                setOpen(true);
              }
            }}
          />
        </div>
        {/* ⚠️ The hex stays, and it is not decoration: it is what somebody pastes in from a brand
            guide, and what they read back out to put in one. */}
        <TextInput
          value={value}
          aria-label={t('common.color.hex')}
          spellCheck={false}
          className="h-8 min-w-0 flex-1 tabular-nums"
          onChange={(event) => {
            const next = event.target.value.trim();
            /* ⚠️ Typed straight through only when it is a colour — a half-typed `#2` would
                 otherwise be stored and repaint the swatch black on every keystroke. */
            if (/^#[0-9A-Fa-f]{6}$/u.test(next)) onChange(next.toUpperCase());
          }}
        />
      </div>

      {open && rect !== null
        ? createPortal(
            <div
              ref={surface}
              role="dialog"
              aria-label={label}
              style={anchorTo(rect, { width: 240 })}
              className="pop-in bg-popover text-popover-foreground shadow-pop rounded-md border p-2"
            >
              <HexColorPicker
                color={value}
                onChange={onChange}
                style={{ width: '100%', height: 150 }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
